import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

function LightboxHeader({ artwork, activeIndex, total, onClose }) {
  return (
    <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.05] bg-[#0a0a0a]">
      <span className="font-mono text-[10px] tracking-[0.2em] text-[#FFC700]/50">
        {artwork.id} &nbsp;—&nbsp; {activeIndex + 1} / {total}
      </span>
      <div className="flex items-center gap-4">
        <span className="font-mono text-[10px] tracking-widest text-white/20 hidden sm:block">
          ESC TO CLOSE
        </span>
        <button
          onClick={onClose}
          className="text-white/30 hover:text-[#FFC700] transition-colors p-1"
          data-testid="lightbox-close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

function LightboxMedia({ artwork, onNav }) {
  return (
    <div className="relative bg-[#050505] flex items-center justify-center" style={{ maxHeight: '50vh' }}>
      {artwork.mediaType === 'video' ? (
        <video
          src={artwork.img}
          autoPlay
          loop
          muted
          playsInline
          className="w-full object-contain"
          style={{ maxHeight: '50vh' }}
          data-testid="lightbox-video"
        />
      ) : (
        <img
          src={artwork.img}
          alt={artwork.title}
          className="w-full object-contain"
          style={{ maxHeight: '50vh' }}
          data-testid="lightbox-image"
        />
      )}

      <button
        onClick={() => onNav(-1)}
        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/70 border border-white/10 text-white/40 hover:text-[#FFC700] hover:border-[#FFC700]/40 transition-all"
        data-testid="lightbox-prev"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        onClick={() => onNav(1)}
        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/70 border border-white/10 text-white/40 hover:text-[#FFC700] hover:border-[#FFC700]/40 transition-all"
        data-testid="lightbox-next"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

function LightboxMeta({ artwork }) {
  return (
    <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 border-t border-white/[0.05] bg-[#0a0a0a]">
      <div>
        <p className="font-mono text-[9px] tracking-[0.2em] text-white/25 mb-1">TITLE</p>
        <p className="font-['Orbitron'] text-sm font-bold text-white leading-tight">{artwork.title}</p>
      </div>
      <div>
        <p className="font-mono text-[9px] tracking-[0.2em] text-white/25 mb-1">YEAR</p>
        <p className="font-mono text-xs text-[#FFC700]/80">{artwork.year || '[PENDING]'}</p>
      </div>
      <div>
        <p className="font-mono text-[9px] tracking-[0.2em] text-white/25 mb-1">TYPE</p>
        <p className="font-mono text-xs text-white/50">{artwork.type}</p>
      </div>
      <div>
        <p className="font-mono text-[9px] tracking-[0.2em] text-white/25 mb-1">STATUS</p>
        <p className="font-mono text-xs text-[#FFC700]/60">{artwork.status}</p>
      </div>
      {artwork.description && (
        <div className="col-span-2 md:col-span-4">
          <p className="font-mono text-[9px] tracking-[0.2em] text-white/25 mb-1">NOTE</p>
          <p className="font-mono text-[10px] text-white/35 leading-relaxed">{artwork.description}</p>
        </div>
      )}
    </div>
  );
}

function LightboxDots({ artworks, activeIndex, onNav }) {
  return (
    <div className="flex justify-center gap-2 py-3 bg-[#050505] border-t border-white/[0.04]">
      {artworks.map((artwork, i) => (
        <button
          key={artwork.id}
          onClick={() => onNav(i - activeIndex)}
          data-testid={`lightbox-dot-${i}`}
          className={`rounded-full transition-all duration-200 ${
            i === activeIndex
              ? 'w-4 h-1.5 bg-[#FFC700]'
              : 'w-1.5 h-1.5 bg-white/20 hover:bg-white/40'
          }`}
        />
      ))}
    </div>
  );
}

export function GalleryLightbox({ artworks, activeIndex, onClose, onNav }) {
  const artwork = artworks[activeIndex];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onNav(-1);
      if (e.key === 'ArrowRight') onNav(1);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onNav]);

  if (!artwork) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 9500, background: 'rgba(5,5,5,0.96)' }}
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="relative w-full max-w-4xl border border-[#FFC700]/20 bg-[#080808] overflow-hidden overflow-y-auto"
        style={{ maxHeight: '92vh' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-content"
      >
        <LightboxHeader artwork={artwork} activeIndex={activeIndex} total={artworks.length} onClose={onClose} />
        <LightboxMedia artwork={artwork} onNav={onNav} />
        <LightboxMeta artwork={artwork} />
        <LightboxDots artworks={artworks} activeIndex={activeIndex} onNav={onNav} />
      </motion.div>
    </motion.div>
  );
}
